import { useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

// Heartbeat interval (in-memory check; does NOT mean a DB write).
const ACTIVITY_UPDATE_INTERVAL = 15 * 60 * 1000; // 15 minutes
const INACTIVE_THRESHOLD = 15 * 60 * 1000;       // 15 minutes — "online" window
// Minimum spacing between DB writes per tab.
const DEBOUNCE_MS = 15 * 60 * 1000;              // 15 minutes
const LS_KEY = 'hive_last_active_write';
// Skip the DB write entirely if any tab/session wrote within this window.
const CROSS_SESSION_THROTTLE_MS = 15 * 60 * 1000; // 15 minutes

export const useActivityTracker = () => {
  const { user } = useAuth();
  const lastUpdateRef = useRef<number>(0);
  const isActiveRef = useRef<boolean>(true);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updateActivity = useCallback(async () => {
    if (!user) return;

    const now = Date.now();
    if (now - lastUpdateRef.current < DEBOUNCE_MS) return;

    // Cross-tab / cross-reload throttle: if any session already wrote
    // last_active_at within the last 5 minutes, the user is clearly
    // "online" and another write would be wasted I/O.
    try {
      const stored = Number(localStorage.getItem(LS_KEY) || 0);
      if (stored && now - stored < CROSS_SESSION_THROTTLE_MS) {
        lastUpdateRef.current = stored;
        return;
      }
    } catch {}

    lastUpdateRef.current = now;
    try { localStorage.setItem(LS_KEY, String(now)); } catch {}

    try {
      await supabase
        .from('profiles')
        .update({ last_active_at: new Date().toISOString() })
        .eq('user_id', user.id);
    } catch (error) {
      console.error('Failed to update activity:', error);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;

    // Update activity on mount (deferred further to not block page load).
    // Pushed out so quick page visits don't trigger a write at all.
    const mountTimer = setTimeout(updateActivity, 15000);

    // Periodic updates at longer intervals
    const intervalId = setInterval(() => {
      if (isActiveRef.current) {
        updateActivity();
      }
    }, ACTIVITY_UPDATE_INTERVAL);

    // Debounced activity handler - only listen to visibility changes, not every interaction
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        isActiveRef.current = true;
        // Debounce the update
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(updateActivity, 5000);
      } else {
        isActiveRef.current = false;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimeout(mountTimer);
      clearInterval(intervalId);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, updateActivity]);
};

// Helper to check if user is online (active within last 5 minutes)
export const isUserOnline = (lastActiveAt: string | null): boolean => {
  if (!lastActiveAt) return false;
  const lastActive = new Date(lastActiveAt).getTime();
  return Date.now() - lastActive < INACTIVE_THRESHOLD;
};

// Helper to format last active time
export const formatLastActive = (lastActiveAt: string | null): string => {
  if (!lastActiveAt) return 'Never';
  
  const lastActive = new Date(lastActiveAt);
  const now = new Date();
  const diffMs = now.getTime() - lastActive.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 5) return 'Active now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return lastActive.toLocaleDateString();
};
